import { Card, CircularProgress, Divider } from "@material-ui/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AmplitudePropertyConstraint,
  type AmplitudeEvent,
  type OneOnOneSessionCanceledProperties,
  type OneOnOneSessionReScheduledProperties,
  type OneOnOneSessionScheduledProperties,
} from "../../../models/amplitude";
import {
  asyncCancelEvent,
  asyncGetUpcomingEvents,
  asyncSaveEvents,
} from "../../../participant/actions/bucketActions";
import {
  CREATE_EVENT_SUCCESS_MESSAGE,
  UPDATE_EVENT_SUCCESS_MESSAGE,
} from "../../../participant/components/participantsEvents/utils";
import {
  DialogPurchaseSessionPoints,
  type MembershipProduct,
  type MembershipProductCategories,
} from "../../../shared/components/purchase-session-points-modal/purchase-session-points-modal.component";
import useModal from "../../../shared/hooks/useModal";
import usePricingModal from "../../../shared/hooks/usePricingModal";
import { type RootState } from "../../../store";
import AboutMe from "../../components/about-me/about-me.component";
import MeetYourMentor from "../../components/meet-your-mentor/meet-your-mentor.component";
import ScheduleForm from "../../components/schedule-form/schedule-form.component";
import {
  DEFAULT_EVENT_STATE,
  eventStatusId,
  eventTypeId1v1,
  scheduleStates,
  type Meeting,
} from "../../components/schedule-one-on-one/schedule-one-on-one.constants";
import { useStyle } from "../../components/schedule-one-on-one/schedule-one-on-one.styles";
import {
  generateStripeCheckoutLink,
  getParticipantPaymentSummary,
  getSessionEventProducts,
} from "../../services";
import ScheduleButton from "./schedule-button.component";
import ScheduleFooter from "./schedule-footer.component";
import { ScheduleSuccess } from "./schedule-success.component";

type Selector = {
  request: RootState["request"]["request"];
  profile: RootState["profile"];
  utcLocal: RootState["bucketReducer"]["workingHours"]["utcLocal"];
  upcomingEvents: RootState["bucketReducer"]["upcomingEvents"];
};

type ChildProps = {
  onClick(): void;
  isButtonDisabled: boolean;
};

type Props = {
  isQuickWin?: boolean;
  setCompletedQuickWin?: (boolean) => void;
  children?: (props: ChildProps) => React.ReactNode;
};

export function ScheduleOneOnOne({
  isQuickWin,
  children,
  setCompletedQuickWin,
}: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { isShowing: showPurchaseDialog, toggle: togglePurchaseDialog } =
    useModal();

  const [event, setEvent] = useState<Meeting>(DEFAULT_EVENT_STATE);
  const [scheduleState, setScheduleState] = useState(
    (isQuickWin && 1) || scheduleStates.init
  );
  const [isSaved, setIsSaved] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isMentorAboutExpanded, setMentorAboutExpanded] = useState(true);

  const { request, profile, utcLocal, upcomingEvents } = useSelector<
    RootState,
    Selector
  >((state) => ({
    request: state.request.request,
    profile: state.profile,
    utcLocal: state.bucketReducer.workingHours.utcLocal,
    upcomingEvents: state.bucketReducer.upcomingEvents,
  }));

  const {
    educatorId,
    educatorName,
    participantProfileId,
    name,
    lastName,
    isPremium,
  } = profile || {};
  const participantName = `${name} ${lastName}`;

  const amplitudeData: AmplitudeEvent<
    | OneOnOneSessionScheduledProperties
    | OneOnOneSessionReScheduledProperties
    | OneOnOneSessionCanceledProperties,
    AmplitudePropertyConstraint.OPTIONAL
  > = {
    user_id: participantProfileId,
    event_properties: {
      page_url: window.location.href,
      mentor_id: educatorId,
      mentor_name: educatorName,
    },
  };

  const { data: participantPaymentSummary } = useQuery({
    queryKey: ["participant-payment-profile"],
    queryFn: getParticipantPaymentSummary,
    refetchOnWindowFocus: false,
  });

  const { data: sessionEventProducts } = useQuery({
    queryKey: ["session-event-products"],
    queryFn: getSessionEventProducts,
    refetchOnWindowFocus: false,
  });

  const { mutate: mutateStripeCheckout } = useMutation(
    generateStripeCheckoutLink,
    {
      onSuccess: (resultUrl) => (window.location.href = resultUrl),
    }
  );

  const getSessionPointsFromProfileSummary = (): number | undefined => {
    const serviceCredits =
      participantPaymentSummary?.creditSummary?.serviceCredits;

    if (serviceCredits) {
      return (
        serviceCredits.find((service) => service.name == "Session Point")
          ?.availableAmount ?? 0
      );
    }

    return undefined;
  };

  const sessionEventProductCategories =
    useMemo((): Array<MembershipProductCategories> => {
      let groupedProducts: Array<MembershipProductCategories> = [];

      if (!sessionEventProducts || sessionEventProducts.length === 0) {
        return groupedProducts;
      }

      groupedProducts = sessionEventProducts.reduce(
        (acc: Array<MembershipProductCategories>, e: MembershipProduct) => {
          let category = acc.find((x) => x.billingCategory === e.category);

          if (!category) {
            category = {
              billingCategory: e.category,
              products: [],
            };
            acc.push(category);
          }

          category.products.push({
            id: e.id,
            name: e.name,
            price: e.price,
          });

          return acc;
        },
        []
      );

      return groupedProducts;
    }, [sessionEventProducts]);

  const sessionPoints = getSessionPointsFromProfileSummary();

  useEffect(() => {
    if (isSubmitted && !request.isFetching && !!request.message) {
      const hasErrorStatus = request.statusCode === 400;
      setHasError(hasErrorStatus);
      if (
        !hasErrorStatus &&
        [CREATE_EVENT_SUCCESS_MESSAGE, UPDATE_EVENT_SUCCESS_MESSAGE].includes(
          request.message
        )
      ) {
        setIsSaved(true);
        setScheduleState(scheduleStates.success);
        setCompletedQuickWin(true);
        setIsSubmitted(false);
      } else {
        setScheduleState(scheduleStates.edit);
        setIsSubmitted(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, isSubmitted, setCompletedQuickWin]);

  const handleExpandToggleClick = () => {
    setMentorAboutExpanded(!isMentorAboutExpanded);
  };

  const handleScheduleFormConfirm = async (meeting: Meeting) => {
    meeting = {
      ...meeting,
      title: `1:1 ${participantName} with ${educatorName}`,
      utcId: utcLocal?.utcId,
    };

    setEvent((current) => ({
      ...current,
      start: meeting.start,
      startHour: meeting.startHour,
      title: meeting.title,
      utcId: meeting.utcId,
      description: meeting.description,
      location: meeting.location,
    }));

    const start = new Date(meeting.start);
    const [hours, minutes] = meeting.startHour.split(":");
    start.setHours(Number(hours), Number(minutes));

    const eventInput = {
      ...event,
      start: meeting.start.toISOString(),
      startHour: meeting.startHour,
      title: meeting.title,
      utcId: meeting.utcId,
      description: meeting.description,
      location: meeting.location,
      guest: { guestList: [{ profileId: educatorId }] },
    };

    setIsSubmitted(true);
    dispatch(
      asyncSaveEvents(
        { eventInput, meeting },
        {
          amplitude: amplitudeData,
        }
      )
    );
    setIsSubmitted(true);
    setEvent((current) => ({
      ...current,
      location: current.location === "Zoom" ? "Zoom" : "Phone call",
    }));

    const appBarHeight =
      document.querySelector(".MuiAppBar-positionFixed")?.clientHeight ?? 60;

    scroll({
      top: containerRef.current?.offsetTop
        ? containerRef.current.offsetTop - appBarHeight - 20
        : 0,
      behavior: "smooth",
    });
  };

  const handleCancelEventClick = () => {
    dispatch(
      asyncCancelEvent({ eventId: event.eventId }, { amplitude: amplitudeData })
    );
    setIsSaved(false);
    setIsSubmitted(false);
    setMentorAboutExpanded(true);
    setEvent(DEFAULT_EVENT_STATE);
    setScheduleState(scheduleStates.init);
  };

  const handleScheduleInitClick = () => {
    if (!isPremium && sessionPoints === 0) {
      togglePricingModal();
      return;
    }
    if (!event.eventId && sessionPoints === 0) {
      togglePurchaseDialog();
      return;
    }
    if (scheduleState === scheduleStates.init) {
      setMentorAboutExpanded(false);
      setScheduleState(scheduleStates.add);
    } else {
      setScheduleState(scheduleStates.edit);
    }
  };

  useEffect(() => {
    dispatch(asyncGetUpcomingEvents());
  }, [dispatch]);

  const { modalComponent: PricingModal, togglePricingModal } =
    usePricingModal();

  const isScheduleDisabled = useMemo(() => {
    if (isSubmitted) {
      return true;
    }
    if (sessionPoints === 0 && !isPremium) {
      return false;
    }
    if (sessionPoints === 0 && !event.eventId) {
      return true;
    }
    if (scheduleState === scheduleStates.init) {
      return false;
    }
    return !(
      !!event.start &&
      !!event.location &&
      event.description?.length >= 2
    );
  }, [isSubmitted, sessionPoints, scheduleState, event, isPremium]);

  const upcomingEvent = useMemo(() => {
    const find = upcomingEvents?.find(
      (current) =>
        current?.eventType?.eventTypeId === eventTypeId1v1 &&
        current?.eventStatus?.eventStatusId === eventStatusId
    );
    if (find) {
      if (!hasError) setScheduleState(scheduleStates.success);
      setMentorAboutExpanded(false);
      setEvent((current) => ({
        ...current,
        eventId: find.eventId,
        start: new Date(find.start),
        title: find.title,
        description: find.resource.description,
        location: find.resource.location,
      }));
    }
    return find;
  }, [upcomingEvents, hasError]);

  const handleScheduleEditClick = () => {
    setScheduleState(scheduleStates.edit);
    setEvent((current) => ({
      ...current,
      eventId: upcomingEvent?.eventId,
    }));
  };

  const handleAddToCart = async (product: MembershipProduct) => {
    togglePurchaseDialog();
    mutateStripeCheckout(product.id);
  };

  const renderInitActions = () =>
    children ? (
      children({
        onClick: handleScheduleInitClick,
        isButtonDisabled: isScheduleDisabled,
      })
    ) : (
      <>
        <ScheduleFooter
          sessionPoints={sessionPoints}
          onPurchaseSessionPoints={togglePurchaseDialog}
        />
        <ScheduleButton
          onClick={handleScheduleInitClick}
          disabled={isScheduleDisabled}
          tooltip={isSubmitted ? "Saving, please wait..." : undefined}
        />
      </>
    );

  return (
    <Card ref={containerRef} className={classes.container} id="mentor-wt-1">
      <MeetYourMentor
        educatorName={educatorName}
        expanded={isMentorAboutExpanded}
        onExpandClick={handleExpandToggleClick}
      />
      <Divider className={classes.divider} />
      {educatorId && (
        <AboutMe expanded={isMentorAboutExpanded} educatorId={educatorId} />
      )}
      {(isSubmitted || !educatorId) && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
      {(isSubmitted || scheduleState === scheduleStates.init) &&
        renderInitActions()}
      {!isSubmitted && scheduleState === scheduleStates.success && event && (
        <>
          <ScheduleSuccess
            isQuickWin={isQuickWin}
            onEditClick={handleScheduleEditClick}
            onCancelEventClick={handleCancelEventClick}
            isSaved={isSaved}
            event={event}
          />
        </>
      )}
      {!isSubmitted &&
        [scheduleStates.add, scheduleStates.edit].includes(scheduleState) && (
          <>
            <ScheduleForm
              upcomingMeeting={event}
              scheduleError={hasError}
              sessionPoints={sessionPoints}
              onConfirmSchedule={handleScheduleFormConfirm}
              onPurchaseSessionPoints={togglePurchaseDialog}
            />
          </>
        )}
      <DialogPurchaseSessionPoints
        products={sessionEventProductCategories}
        isOpen={showPurchaseDialog}
        onClose={() => togglePurchaseDialog()}
        onAddToCart={handleAddToCart}
      />
      {PricingModal}
    </Card>
  );
}

ScheduleOneOnOne.defaultProps = {
  isQuickWin: false,
  children: null,
  setCompletedQuickWin: () => null,
};
